// local imports
import { DynamicCohortQuery } from "../../../pages/Administration/Engagement/Components/DynamicEmployeesQueryBuilder/DynamicEmployeesUtils";

import request from "../base-request";

// TODO - use the models package, but that breaks everything until we upgrade
// to the latest version of react.
export interface ICohortModel {
	type: "static" | "dynamic";
	name: string;
	description: string;
	_id: string;
	color: string;
	query?: DynamicCohortQuery;
	memberCount: number;
	calculation_status?: "processing" | "backpropagating" | "idle";
}

interface IGetCohortResponse extends ICohortModel {
	createdAt: string;
	updatedAt: string;
}

/**
 * Returns array of all cohorts in the user's company
 */
export const fetchCohorts = async () => {
	const [data] = await request<{
		cohorts: IGetCohortResponse[];
		maximumAllowedCohorts: number;
		currentCohortCount: number;
	}>("/api/engagement/cohorts/");

	const processedCohorts = data.cohorts.map((cohort) => {
		return {
			...cohort,
			createdAt: new Date(cohort.createdAt),
			updatedAt: new Date(cohort.updatedAt),
		};
	});

	const { maximumAllowedCohorts, currentCohortCount } = data;

	return { cohorts: processedCohorts, maximumAllowedCohorts, currentCohortCount };
};

/**
 * Fetches a single cohort by id
 */
export const fetchCohort = async (cohortID: string) => {
	const [data] = await request<{
		cohort: IGetCohortResponse;
	}>(`/api/engagement/cohorts/${cohortID}/`);

	const cohort = {
		...data.cohort,
		createdAt: new Date(data.cohort.createdAt),
		updatedAt: new Date(data.cohort.updatedAt),
	};

	return { cohort };
};

/**
 * Creates a new static cohort. Static cohorts are created from a user selection object.
 */
export const createStaticCohort = async (
	name: string,
	description: string,
	color: string,
	selectionObjectID: string
) => {
	const [data] = await request<any>("/api/engagement/cohorts/", {
		method: "POST",
		body: JSON.stringify({ name, description, type: "static", color, selectionObjectID: selectionObjectID }),
	});

	return data;
};

/**
 * Creates a new dynamic cohort. Dynamic cohorts are created from a query.
 * TODO - use models package to type the query
 */
export const createDynamicCohort = async (
	name: string,
	description: string,
	color: string,
	query: Record<any, any>
) => {
	const [data] = await request<any>("/api/engagement/cohorts/", {
		method: "POST",
		body: JSON.stringify({ name, description, type: "dynamic", color, query }),
	});

	return data;
};

/**
 * Returns a user selection object from a static cohort. This is useful when
 * editing a static cohort.
 */
export const createUserSelectionFromCohort = async (cohortID: string) => {
	const [data] = await request<{ user_selection: string }>(`/api/engagement/cohorts/${cohortID}/user-selection/`, {
		method: "POST",
	});

	return data;
};

/**
 * Updates a static cohort. This can be the name, description, color, or user
 * selection object.
 */
export const updateCohort = async (
	cohortID: string,
	updates: {
		name?: string;
		color?: string;
		description?: string;
		addUserSelectionID?: string;
		removeUserSelectionID?: string;
		query?: DynamicCohortQuery;
	}
) => {
	const [data] = await request<{ success: boolean }>(`/api/engagement/cohorts/${cohortID}/`, {
		method: "PUT",
		body: JSON.stringify(updates),
	});

	return data;
};

/**
 * Marks a cohort as deleted. This does not actually delete the cohort, but it
 * updates the deleted_at property.
 */
export const deleteCohort = async (cohortID: string) => {
	const [data] = await request<{ success: boolean }>(`/api/engagement/cohorts/${cohortID}/`, {
		method: "DELETE",
	});

	return data;
};
